import { useState, useRef, useEffect } from "react";
import { visualizerActions,uiActions } from '../../store';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { generateShareLink } from "../../Helpers/ShareHelper";

import PrintLink from '../PrintLink/PrintLink';

import styles from './TopMenu.module.css';

import closeIcon from './assets/close.svg';
import openIcon from './assets/open.svg';
import { removeUnusedPatternsFromClone } from "../../Helpers/SvgHelper";
import { ChamUploadButton } from '../ChamUpload/ChamUpload';
import { postSaveToParent, generateSavePayload } from '../../Helpers/PostMessageHelper';



export default function TopMenu({ extended }) {

    // Holds the mobile menu open/closed status
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [linkCopyText, setLinkCopyText] = useState('Copy Link');
    const linkInputRef = useRef(null);
    const menuRef = useRef(null);
    extended = true;

    const [downloadBtnStatus, setDownloadBtnStatus] = useState("Download Pattern Image");

    const dispatch = useDispatch();
    const activePattern = useSelector((state) => state.visualizer.activePattern);
    const selectedSlotProducts = useSelector(state => state.visualizer.selectedSlotProducts);

    // Update download button text based on current page
    useEffect(() => {
        if (window.location.href.indexOf("roomview") !== -1) {
            setDownloadBtnStatus("Download Room Image");
        } else {
            setDownloadBtnStatus("Download Pattern Image");
        }
    }, []);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target) && isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobileMenuOpen]);

    // Update mobile menu status on the hamburger icon click
    const handleMenuTriggerClick = function () {
        setIsMobileMenuOpen(status => !status);
    }

    // Close menu when a menu item is clicked
    const closeMenu = () => {
        setIsMobileMenuOpen(false);
    }

    const handleShareModalButtonClick = function () {
        // Get the current page URL
        const currentUrl = window.location.href;
        
        // Store the current URL in the share data
        dispatch(visualizerActions.setShareData({
            url: currentUrl,
            specifier: "",
            sel: -1,
            message: "Check out this design I made using the Amtico Visualizer!"
        }));
        
        // Open the share modal
        dispatch(uiActions.setShareModalActive(true));
        closeMenu();
    }

    const handleHelpModalButtonClick = function () {
        dispatch(uiActions.setHelpModalActive(true));
        closeMenu();
    }


    const handleFeedbackModalButtonClick = function () {
        dispatch(uiActions.setFeedbackModalActive(true));
        closeMenu();
    }

    const handleSaveButtonClick = function () {
        // Generate the save payload
        const payload = generateSavePayload(activePattern, selectedSlotProducts);
        
        if (payload) {
            // Send post message to parent window
            postSaveToParent(payload);
            console.log('Project saved:', payload);
        } else {
            console.error('Failed to generate save payload');
        }
        
        closeMenu();
    }

    // Async function to download image from URL
    async function downloadImage(imageSrc, nameOfDownload = 'my-image.jpeg') {
        try {
            // 1. Fetch the image data from the source URL
            const response = await fetch(imageSrc);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            // 2. Get the response as a Blob (Binary Large Object)
            const blobImage = await response.blob();
            
            // 3. Create a temporary URL for the blob
            const href = URL.createObjectURL(blobImage);
            
            // 4. Create a temporary anchor element
            const anchorElement = document.createElement('a');
            anchorElement.href = href;
            anchorElement.download = nameOfDownload; // Set the desired file name
            
            // 5. Append the anchor to the body, click it, and remove it
            document.body.appendChild(anchorElement);
            anchorElement.click(); // Trigger the download
            document.body.removeChild(anchorElement);
            
            // 6. Revoke the temporary object URL to free up memory
            window.URL.revokeObjectURL(href);
            console.log('The image has been downloaded');
        } catch (error) {
            console.error('Error downloading image: ', error);
            throw error; // Re-throw to handle in calling function
        }
    }

    const handleDownloadButtonClick = async function () {
        const isRoomView = window.location.href.indexOf("roomview") !== -1;
        const loadingText = isRoomView ? "Loading Room Image..." : "Loading Pattern Image...";
        const defaultText = isRoomView ? "Download Room Image" : "Download Pattern Image";
        
        setDownloadBtnStatus(loadingText);

        try {
            if(isRoomView){
                // Download room view image
                const roomImage = document.getElementById("roomViewMain");
                if (!roomImage) {
                    console.error('Room view image not found');
                    setDownloadBtnStatus(defaultText);
                    return;
                }
                
                const imageUrl = roomImage.src;
                
                // Get pattern name for filename
                const patternName = activePattern?.name || activePattern?.patternName || 'Room Render';
                const fileName = `${patternName.replace(/[^a-z0-9]/gi, '_')}.jpg`;
                
                await downloadImage(imageUrl, fileName);
                setDownloadBtnStatus(defaultText);
            }
            else{
                // 1. Get the img src url from PatternView_patternImage
                const patternImage = document.querySelector('[class*="patternImage"]') || 
                                   document.querySelector('img[alt*="Pattern"]') ||
                                   document.querySelector('.PatternView_patternImage');
                
                if (!patternImage) {
                    console.error('Pattern image not found');
                    setDownloadBtnStatus(defaultText);
                    return;
                }

                const imageUrl = patternImage.src;
                if (!imageUrl) {
                    console.error('Pattern image URL not found');
                    setDownloadBtnStatus(defaultText);
                    return;
                }

                // Get pattern name for filename
                const patternName = activePattern?.name || activePattern?.patternName || 'Pattern';
                const fileName = `${patternName.replace(/[^a-z0-9]/gi, '_')}.jpg`;

                // 2. Download the image using the async function
                await downloadImage(imageUrl, fileName);
                setDownloadBtnStatus(defaultText);
                console.log(`Pattern image downloaded successfully as: ${fileName}`);
            }
        } catch (error) {
            console.error('Error in download function:', error);
            setDownloadBtnStatus(defaultText);
        }
        closeMenu();
    }

    const handleHelpButtonClick = function () {
        dispatch(uiActions.setVisualizerHelpModalActive(true));
        console.log("show visualizer help");
        closeMenu();
    }


    return (
        <section className={styles.topMenu} ref={menuRef}>
            <nav aria-label="Main Navigation">
                <ul className={!isMobileMenuOpen ? styles.closed : ''}>

                    <li className={styles.menuTrigger}>
                        <button onClick={handleMenuTriggerClick} aria-expanded={isMobileMenuOpen} aria-controls="main-menu">
                            <span aria-hidden="true">
                                {!isMobileMenuOpen && <img src={openIcon} alt='open icon' />}
                                {isMobileMenuOpen && <img src={closeIcon} alt='close icon' />}
                            </span>
                        </button>
                    </li>

                    

                    <div className={styles.rightNav}>

                        {/* {window.location.href.indexOf("roomview") !== -1 &&
                            <li className={styles.saveScene}>
                            <button type="button" onClick={saveRoomsceneToBoard} aria-label="Save roomscene">
                                <span>Save room scene to board</span>
                            </button>
                        </li>
                        } */}

                        {extended && (<>

                             <li>
                                <button type="button" onClick={handleSaveButtonClick} aria-label="Save">
                                    <span>Save</span>
                                </button>
                            </li>

                            <li className={styles.altNav}>
                                <button type="button" onClick={handleShareModalButtonClick} aria-label="Share">
                                    <span>Share Project Link</span>
                                </button>
                            </li>

                            <li className={styles.altNav}>
                                <button type="button" onClick={handleDownloadButtonClick} aria-label="Download">
                                    <span>{downloadBtnStatus}</span>
                                </button>
                            </li>

                            <li className={styles.altNav}>
                                <PrintLink onClick={closeMenu} />
                            </li>

                            {/* <li>
                                 <ChamUploadButton title="Upload Roomscene" className="" aria-label="Upload Button" onClick={closeMenu} />
                            </li> */}

                            <li>
                                <button type="button" onClick={handleFeedbackModalButtonClick} aria-label="Feedback">
                                    <span>Feedback</span>
                                </button>
                            </li>

                            <li>
                                <button onClick={handleHelpButtonClick}>Help</button>
                            </li>

                        </>

                        )}
                    </div>

                </ul>
            </nav>
        </section>
    );
}

